import { MAX_GPX_BYTES, readFileAsText } from "./utils.js";
import { buildViewerProfile } from "./profile.js";

export async function loadGpxFromFile(file) {
  const gpxText = await readFileAsText(file);
  return loadGpxFromText(gpxText);
}

export async function loadGpxFromUrl(urlValue) {
  const url = normalizeGpxUrl(urlValue);
  const response = await fetch(url.href, { credentials: "same-origin" });

  if (!response.ok) {
    throw new Error(`Unable to fetch GPX file (${response.status}).`);
  }

  const contentLength = response.headers.get("Content-Length");
  if (contentLength && Number(contentLength) > MAX_GPX_BYTES) {
    throw new Error("The GPX file is too large.");
  }

  const gpxText = await response.text();
  if (gpxText.length > MAX_GPX_BYTES) {
    throw new Error("The GPX file is too large.");
  }

  return loadGpxFromText(gpxText);
}

function loadGpxFromText(gpxText) {
  const xml = new DOMParser().parseFromString(gpxText, "application/xml");
  if (xml.querySelector("parsererror")) {
    throw new Error("Invalid GPX XML.");
  }

  return buildViewerProfile(parseTrackPointGroups(xml));
}

function parseTrackPointGroups(xml) {
  const groups = getPointGroups(xml);

  if (!groups.length) {
    throw new Error("The GPX file needs at least two track or route points with coordinates.");
  }

  return groups;
}

function getPointGroups(xml) {
  const trackSegmentGroups = getNodes(xml, "trkseg")
    .map((segment) => parsePointNodes(segment, "trkpt"))
    .filter((points) => points.length >= 2);

  if (trackSegmentGroups.length) {
    return trackSegmentGroups;
  }

  const trackGroups = getNodes(xml, "trk")
    .map((track) => parsePointNodes(track, "trkpt"))
    .filter((points) => points.length >= 2);

  if (trackGroups.length) {
    return trackGroups;
  }

  const routeGroups = getNodes(xml, "rte")
    .map((route) => parsePointNodes(route, "rtept"))
    .filter((points) => points.length >= 2);

  if (routeGroups.length) {
    return routeGroups;
  }

  const trackPoints = parsePointNodes(xml, "trkpt");
  if (trackPoints.length >= 2) {
    return [trackPoints];
  }

  const routePoints = parsePointNodes(xml, "rtept");
  if (routePoints.length >= 2) {
    return [routePoints];
  }

  return [];
}

function parsePointNodes(root, tagName) {
  return getNodes(root, tagName).map((node) => {
    const elevationNode = getNodes(node, "ele")[0];
    const timeNode = getNodes(node, "time")[0];
    const latitude = Number(node.getAttribute("lat"));
    const longitude = Number(node.getAttribute("lon"));
    const elevationValue = elevationNode ? Number(elevationNode.textContent) : NaN;
    const timeValue = timeNode ? new Date(timeNode.textContent) : null;
    const elevation = Number.isFinite(elevationValue) ? elevationValue : null;
    const time = timeValue instanceof Date && !Number.isNaN(timeValue.getTime()) ? timeValue : null;
    return { latitude, longitude, elevation, smoothedElevation: elevation, time, distance: 0 };
  }).filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude));
}

function getNodes(root, tagName) {
  const namespaced = root.getElementsByTagNameNS
    ? Array.from(root.getElementsByTagNameNS("*", tagName))
    : [];

  return namespaced.length ? namespaced : Array.from(root.getElementsByTagName(tagName));
}

function normalizeGpxUrl(urlValue) {
  if (!urlValue || urlValue.length > 2048) {
    throw new Error("Invalid GPX URL.");
  }

  let url;
  try {
    url = new URL(urlValue, window.location.origin);
  } catch (error) {
    throw new Error("Invalid GPX URL.");
  }

  if (url.origin !== window.location.origin) {
    throw new Error("Only GPX files from this site can be loaded.");
  }

  if (!["http:", "https:"].includes(url.protocol)) {
    throw new Error("Invalid GPX URL protocol.");
  }

  if (!url.pathname.toLowerCase().endsWith(".gpx")) {
    throw new Error("The URL must point to a .gpx file.");
  }

  return url;
}
